import React, { useState, useEffect, useRef } from 'react';
import { TimeEntry } from '../types/TimeEntry';
import { timeRegistrationService } from '../services/TimeRegistrationService';
import { useAuth } from '../contexts/AuthContext';
import './TimeCalendar.css';

interface TimeCalendarProps {
  onDayClick?: (date: string, entries: TimeEntry[]) => void;
  onAddEntry?: (date: string, existingEntries: TimeEntry[]) => void;
  refreshToken?: number;
  // Marker on today's cell, e.g. waiting Copilot suggestions
  todayMarker?: { label: string; title?: string; onClick: () => void } | null;
}

const TimeCalendar: React.FC<TimeCalendarProps> = ({ onDayClick, onAddEntry, refreshToken, todayMarker }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'week' | 'month'>('week');
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getAccessToken, isAuthenticated } = useAuth();
  const hasLoadedOnce = useRef(false);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Get the Monday-Sunday range of the week containing the given date
  const getWeekRange = (date: Date): { start: Date; end: Date } => {
    const start = new Date(date);
    const dayOfWeek = start.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    start.setDate(start.getDate() - daysToSubtract);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return { start, end };
  };

  // Distinct year/month pairs touched by the visible week (it may span two months)
  const getWeekMonths = (): { year: number; month: number }[] => {
    const { start, end } = getWeekRange(currentDate);
    const months = new Map<string, { year: number; month: number }>();
    [start, end].forEach(d => {
      months.set(`${d.getFullYear()}-${d.getMonth()}`, { year: d.getFullYear(), month: d.getMonth() });
    });
    return Array.from(months.values());
  };

  // Load time entries for the visible period (week or month)
  useEffect(() => {
    if (isAuthenticated) {
      loadTimeEntries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, viewMode, isAuthenticated]);

  // Silently refresh in the background after an entry is added/edited/deleted elsewhere,
  // without flashing the full skeleton loader (skip the very first mount, effect above covers it)
  useEffect(() => {
    if (isAuthenticated && hasLoadedOnce.current) {
      loadTimeEntries(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const loadTimeEntries = async (silent: boolean = false) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        setError('Failed to get access token');
        return;
      }

      const monthsToLoad = viewMode === 'week' ? getWeekMonths() : [{ year, month }];
      const entriesPerMonth = await Promise.all(
        monthsToLoad.map(({ year: y, month: m }) => timeRegistrationService.getTimeEntries(y, m, accessToken))
      );
      const merged = new Map<number, TimeEntry>();
      entriesPerMonth.flat().forEach(entry => merged.set(entry.Id, entry));
      setTimeEntries(Array.from(merged.values()));
      hasLoadedOnce.current = true;
    } catch (err: any) {
      console.error('Error loading time entries:', err);
      // Don't show interaction_in_progress error to user
      const errorMessage = err instanceof Error ? err.message : 'Failed to load time entries';
      if (!errorMessage.includes('interaction_in_progress')) {
        setError(errorMessage);
      }
    } finally {
      if (silent) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  // Group entries by date
  const entriesByDate = timeRegistrationService.groupEntriesByDate(timeEntries);

  // Get calendar days (starts on Monday)
  const getCalendarDays = () => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    const endDate = new Date(lastDay);
    
    // Start from Monday of the week containing the first day
    // getDay() returns 0 for Sunday, so Monday is 1
    const dayOfWeek = startDate.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Adjust for Monday start
    startDate.setDate(startDate.getDate() - daysToSubtract);
    
    // End on Sunday of the week containing the last day
    endDate.setDate(endDate.getDate() + ((7 - endDate.getDay()) % 7));

    const days = [];
    const current = new Date(startDate);

    while (current <= endDate) {
      // Create date string from local date values to avoid timezone issues
      const currentYear = current.getFullYear();
      const currentMonth = String(current.getMonth() + 1).padStart(2, '0');
      const currentDay = String(current.getDate()).padStart(2, '0');
      const dateStr = `${currentYear}-${currentMonth}-${currentDay}`;
      
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      
      const isCurrentMonth = current.getMonth() === month;
      const isToday = dateStr === todayStr;
      const dayEntries = entriesByDate[dateStr] || [];
      const totalHours = timeRegistrationService.calculateDayTotal(dayEntries);
      const isWeekend = current.getDay() === 0 || current.getDay() === 6; // Sunday or Saturday

      days.push({
        date: dateStr,
        day: current.getDate(),
        isCurrentMonth,
        isToday,
        totalHours,
        entries: dayEntries,
        isWeekend
      });

      current.setDate(current.getDate() + 1);
    }

    return days;
  };

  // Get the 7 days (Mon-Sun) of the week containing currentDate, regardless of month boundaries
  const getWeekDays = () => {
    const { start } = getWeekRange(currentDate);
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    const days = [];
    const current = new Date(start);

    for (let i = 0; i < 7; i++) {
      const currentYear = current.getFullYear();
      const currentMonth = String(current.getMonth() + 1).padStart(2, '0');
      const currentDay = String(current.getDate()).padStart(2, '0');
      const dateStr = `${currentYear}-${currentMonth}-${currentDay}`;

      const isToday = dateStr === todayStr;
      const dayEntries = entriesByDate[dateStr] || [];
      const totalHours = timeRegistrationService.calculateDayTotal(dayEntries);
      const isWeekend = current.getDay() === 0 || current.getDay() === 6;

      days.push({
        date: dateStr,
        day: current.getDate(),
        isCurrentMonth: true,
        isToday,
        totalHours,
        entries: dayEntries,
        isWeekend
      });

      current.setDate(current.getDate() + 1);
    }

    return days;
  };

  const navigate = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (direction === 'prev' ? -7 : 7));
    } else if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  const handleDayClick = (dayInfo: any) => {
    if (dayInfo.isCurrentMonth && onDayClick) {
      onDayClick(dayInfo.date, dayInfo.entries);
    }
  };

  const handleEntryClick = (event: React.MouseEvent, entry: TimeEntry) => {
    event.stopPropagation();
    if (onDayClick) {
      onDayClick(entry.Date, [entry]);
    }
  };

  const handleAddClick = (event: React.MouseEvent, date: string, dayEntries: TimeEntry[]) => {
    event.stopPropagation();
    if (onAddEntry) {
      onAddEntry(date, dayEntries);
    }
  };

  const formatMonthYear = (date: Date) => {
    const label = date.toLocaleDateString('sv-SE', { 
      month: 'long', 
      year: 'numeric' 
    });
    return label.charAt(0).toUpperCase() + label.slice(1);
  };

  // Get Swedish holidays
  const getSwedishHolidays = (): { [key: string]: string } => {
    const year = currentDate.getFullYear();
    const holidays: { [key: string]: string } = {};
    
    // Fixed holidays
    const fixedHolidays = [
      { date: '01-01', name: 'Nyårsdagen' },
      { date: '01-06', name: 'Trettondedag jul' },
      { date: '04-30', name: 'Valborgsmässoafton' },
      { date: '05-01', name: 'Första maj' },
      { date: '06-06', name: 'Sveriges nationaldag' },
      { date: '11-02', name: 'Alla själars dag' },
      { date: '12-25', name: 'Första juldagen' },
      { date: '12-26', name: 'Andra juldagen' }
    ];

    // Add fixed holidays
    fixedHolidays.forEach(holiday => {
      const key = `${year}-${holiday.date}`;
      holidays[key] = holiday.name;
    });

    // Easter and movable holidays (calculated)
    const easter = getEasterDate(year);
    const easterDate = new Date(easter);
    
    // Maundy Thursday (3 days before Easter)
    const maundyThursday = new Date(easterDate);
    maundyThursday.setDate(maundyThursday.getDate() - 3);
    holidays[formatDateToKey(maundyThursday)] = 'Skärtorsdagen';
    
    // Good Friday (2 days before Easter)
    const goodFriday = new Date(easterDate);
    goodFriday.setDate(goodFriday.getDate() - 2);
    holidays[formatDateToKey(goodFriday)] = 'Långfredagen';
    
    // Easter Sunday
    holidays[formatDateToKey(easterDate)] = 'Påskdagen';
    
    
    // Easter Monday
    const easterMonday = new Date(easterDate);
    easterMonday.setDate(easterMonday.getDate() + 1);
    holidays[formatDateToKey(easterMonday)] = 'Andra påskdagen';
    
    // Ascension Day (39 days after Easter)
    const ascensionDay = new Date(easterDate);
    ascensionDay.setDate(ascensionDay.getDate() + 39);
    holidays[formatDateToKey(ascensionDay)] = 'Kristi himmelsfärd';
    
    // Whit Sunday (49 days after Easter)
    const whitSunday = new Date(easterDate);
    whitSunday.setDate(whitSunday.getDate() + 49);
    holidays[formatDateToKey(whitSunday)] = 'Pingstdagen';
    
    // Midsummer Eve (Friday between June 19-25)
    const midsummerEve = getMidsummerEve(year);
    holidays[formatDateToKey(midsummerEve)] = 'Midsommarafton';
    
    // Midsummer Day (day after Midsummer Eve)
    const midsummerDay = new Date(midsummerEve);
    midsummerDay.setDate(midsummerDay.getDate() + 1);
    holidays[formatDateToKey(midsummerDay)] = 'Midsommardagen';
    
    return holidays;
  };

  // Calculate Easter date using the Meeus/Jones/Butcher algorithm
  const getEasterDate = (year: number): Date => {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month - 1, day);
  };

  // Get Midsummer Eve (Friday between June 19-25)
  const getMidsummerEve = (year: number): Date => {
    const june19 = new Date(year, 5, 19); // June 19
    const dayOfWeek = june19.getDay();
    // Friday is 5, so we need to find the Friday between June 19-25
    let daysToAdd = (5 - dayOfWeek + 7) % 7;
    if (daysToAdd === 0) daysToAdd = 0; // If June 19 is Friday, use it
    return new Date(year, 5, 19 + daysToAdd);
  };

  // Format date to YYYY-MM-DD key
  const formatDateToKey = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get Swedish week number (ISO 8601)
  const getWeekNumber = (dateStr: string): number => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    
    // Get Thursday of this week
    const d = new Date(date);
    const dayOfWeek = d.getDay();
    const diff = d.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Adjust when day is Sunday
    const thursday = new Date(d.setDate(diff + 3));
    
    // Get January 4th of the same year as thursday
    const jan4 = new Date(thursday.getFullYear(), 0, 4);
    
    // Get the Monday of the week containing January 4th
    const dayOfWeekJan4 = jan4.getDay();
    const diffJan4 = jan4.getDate() - dayOfWeekJan4 + (dayOfWeekJan4 === 0 ? -6 : 1);
    const mondayWeek1 = new Date(jan4.getFullYear(), 0, diffJan4);
    
    // Calculate week number
    const msPerDay = 24 * 60 * 60 * 1000;
    const weekNum = Math.floor((thursday.getTime() - mondayWeek1.getTime()) / msPerDay / 7) + 1;
    
    return weekNum;
  };

  // Format the week header label, e.g. "Vecka 38 · 15 sep – 21 sep 2026"
  const formatWeekLabel = (date: Date): string => {
    const { start, end } = getWeekRange(date);
    const weekNum = getWeekNumber(formatDateToKey(start));
    const startLabel = start.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' });
    const endLabel = end.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', year: 'numeric' });
    return `Vecka ${weekNum} · ${startLabel} – ${endLabel}`;
  };

  const calendarDays = viewMode === 'week' ? getWeekDays() : getCalendarDays();
  const periodTotal = calendarDays.reduce((sum, day) => sum + day.totalHours, 0);
  const swedishHolidays = getSwedishHolidays();

  // Calculate total hours per ISO week
  const weekTotals: { [weekKey: string]: number } = {};
  calendarDays.forEach(day => {
    const weekNum = getWeekNumber(day.date);
    const weekYear = (() => {
      const [y, m, d] = day.date.split('-').map(Number);
      const date = new Date(y, m - 1, d);
      const thursday = new Date(date);
      const dow = thursday.getDay();
      thursday.setDate(thursday.getDate() - dow + (dow === 0 ? -6 : 1) + 3);
      return thursday.getFullYear();
    })();
    const key = `${weekYear}-${weekNum}`;
    weekTotals[key] = (weekTotals[key] || 0) + day.totalHours;
  });

  // Show loading skeleton while loading
  if (loading) {
    return (
      <div className={`time-calendar ${viewMode === 'week' ? 'week-view' : ''}`}>
        <div className="calendar-header">
          <div className="month-navigation">
            <button className="nav-button" disabled>← Föregående</button>
            <div className="month-title skeleton-title" />
            <button className="nav-button" disabled>Nästa →</button>
          </div>
          
          <div className="month-summary">
            <span className="total-hours">
              <span className="skeleton-text" style={{ width: '150px' }}></span>
            </span>
          </div>
        </div>

        <div className={`skeleton-calendar-grid ${viewMode === 'week' ? 'week-view' : ''}`}>
          {viewMode !== 'week' && (
            <div className="weekdays">
              <div className="week-number-header">V</div>
              {['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'].map(day => (
                <div key={day} className="weekday skeleton-weekday">
                  {day}
                </div>
              ))}
            </div>
          )}

          <div className="calendar-days">
            {Array.from({ length: viewMode === 'week' ? 7 : 35 }).map((_, index) => (
              <div key={index} className="calendar-day skeleton-day">
                <div className="day-header">
                  <div className="skeleton-day-number"></div>
                </div>
                <div className="day-entries">
                  <div className="skeleton-entry"></div>
                  <div className="skeleton-entry"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`time-calendar ${viewMode === 'week' ? 'week-view' : ''}`}>
      <div className="calendar-header">
        <div className="month-navigation">
          <button 
            onClick={() => navigate('prev')} 
            className="nav-button"
            disabled={loading}
          >
            ← {viewMode === 'week' ? 'Föregående' : 'Föregående'}
          </button>
          <h2 className="month-title">
            {viewMode === 'week' ? formatWeekLabel(currentDate) : formatMonthYear(currentDate)}
          </h2>
          <button 
            onClick={() => navigate('next')} 
            className="nav-button"
            disabled={loading}
          >
            {viewMode === 'week' ? 'Nästa' : 'Nästa'} →
          </button>
        </div>

        <div className="header-actions">
          <div className="calendar-view-toggle">
            <button
              className={`calendar-view-toggle-button ${viewMode === 'week' ? 'active' : ''}`}
              onClick={() => setViewMode('week')}
              disabled={loading}
            >
              Vecka
            </button>
            <button
              className={`calendar-view-toggle-button ${viewMode === 'month' ? 'active' : ''}`}
              onClick={() => setViewMode('month')}
              disabled={loading}
            >
              Månad
            </button>
          </div>

          <div className="month-summary">
            <span className="total-hours">
              {viewMode === 'week' ? 'Timmar denna vecka' : 'Totala timmar'}: <strong>{periodTotal.toFixed(1)}h</strong>
            </span>
            {refreshing && <span className="refreshing-indicator">Uppdaterar…</span>}
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <strong>Fel:</strong> {error}
          <button onClick={() => loadTimeEntries()} className="retry-button">
            Försök igen
          </button>
        </div>
      )}

      <div className={`calendar-grid ${viewMode === 'week' ? 'week-view' : ''} ${refreshing ? 'is-refreshing' : ''}`}>
        {viewMode !== 'week' && (
          <div className="weekdays">
            <div className="week-number-header">V</div>
            {['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'].map(day => (
              <div key={day} className="weekday">
                {day}
              </div>
            ))}
          </div>
        )}

        <div className="calendar-days">
          {calendarDays.map((dayInfo, index) => {
            const weekdayNames = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'];
            const weekdayLabel = weekdayNames[index % 7];
            const isMonday = new Date(dayInfo.date).getDay() === 1;
            const weekNum = getWeekNumber(dayInfo.date);
            
            const weekYear = (() => {
              const [y, m, d] = dayInfo.date.split('-').map(Number);
              const date = new Date(y, m - 1, d);
              const thursday = new Date(date);
              const dow = thursday.getDay();
              thursday.setDate(thursday.getDate() - dow + (dow === 0 ? -6 : 1) + 3);
              return thursday.getFullYear();
            })();
            const weekKey = `${weekYear}-${weekNum}`;
            const weekTotal = weekTotals[weekKey] || 0;

            return (
              <React.Fragment key={index}>
                {isMonday && viewMode !== 'week' && (
                  <div className="week-number">
                    <span className="week-num-label">{weekNum}</span>
                    {weekTotal > 0 && <span className="week-total-hours">{weekTotal.toFixed(1)}h</span>}
                  </div>
                )}
                <div
                  className={`calendar-day ${dayInfo.isCurrentMonth ? 'current-month' : 'other-month'} ${
                    dayInfo.isToday ? 'today' : ''
                  } ${dayInfo.totalHours > 0 ? 'has-hours' : ''} ${swedishHolidays[dayInfo.date] ? 'holiday' : ''} ${dayInfo.isWeekend ? 'weekend' : ''}`}
                  onClick={() => handleDayClick(dayInfo)}
                  title={swedishHolidays[dayInfo.date] || ''}
                >
                  <div className="day-header">
                    <div className="day-number">
                      {viewMode === 'week' && <span className="day-weekday-label">{weekdayLabel} </span>}
                      {dayInfo.day}
                    </div>
                    <div className="day-holiday-status">
                      {swedishHolidays[dayInfo.date] && (
                        <div className="holiday-badge" title={swedishHolidays[dayInfo.date]}>
                          🇸🇪
                        </div>
                      )}
                      {dayInfo.totalHours > 0 && (
                        <div className="day-total">
                          {dayInfo.totalHours.toFixed(1)}h
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="day-entries">
                    {dayInfo.entries.map((entry: TimeEntry) => {
                      const label = `${entry.Customer.Title}${entry.Project ? ` - ${entry.Project.Title}` : ''} - ${entry.Activity.Title} (${entry.Hours}h)`;
                      return (
                        <div
                          key={entry.Id}
                          className="entry-line"
                          onClick={(e) => handleEntryClick(e, entry)}
                          title={label}
                        >
                          {label}
                        </div>
                      );
                    })}
                    
                    {dayInfo.isToday && todayMarker && (
                      <button
                        className="today-marker"
                        onClick={(e) => {
                          e.stopPropagation();
                          todayMarker.onClick();
                        }}
                        title={todayMarker.title || todayMarker.label}
                      >
                        {todayMarker.label}
                      </button>
                    )}

                    {dayInfo.isCurrentMonth && (
                      <button
                        className="add-entry-btn"
                        onClick={(e) => handleAddClick(e, dayInfo.date, dayInfo.entries)}
                        title="Lägg till ny registrering"
                      >
                        +
                      </button>
                    )}
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TimeCalendar;